import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  Play, FileText, Star, Award, CheckCircle, ChevronLeft, ChevronRight,
  Menu, X, BookOpen, Clock, AlertCircle, Sparkles,
  HelpCircle, BookText, Send, Shield, ChevronDown,
  MessageCircle, Minimize2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LESSON_TYPE_ICON = { video: Play, lectura: FileText, test: Star, proyecto: Award };
const LESSON_TYPE_LABEL = { video: 'Vídeo', lectura: 'Lectura', test: 'Test', proyecto: 'Proyecto' };

// ─── Video Player ──────────────────────────────────────────────────────────
function VideoPlayer({ url, onWatched }) {
  const [watched, setWatched] = useState(false);
  if (!url) return (
    <div className="aspect-video bg-black/60 rounded-xl flex items-center justify-center">
      <p className="text-[#94A3B8] text-sm">Vídeo próximamente disponible</p>
    </div>
  );
  const handleMark = () => {
    if (!watched) { setWatched(true); onWatched && onWatched(); }
  };
  return (
    <div className="space-y-3">
      <div className="aspect-video bg-black rounded-xl overflow-hidden">
        <iframe src={url} className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen title="Lección en vídeo" />
      </div>
      {!watched && (
        <button onClick={handleMark} data-testid="mark-video-watched"
          className="w-full py-2.5 rounded-xl border border-emerald-500/40 text-emerald-400 text-sm font-medium hover:bg-emerald-500/10 transition">
          Marcar como visto
        </button>
      )}
      {watched && (
        <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium justify-center">
          <CheckCircle size={15} /> Vídeo marcado como visto
        </div>
      )}
    </div>
  );
}

// ─── Reading Content ───────────────────────────────────────────────────────
function ReadingContent({ content, onRead }) {
  const [read, setRead] = useState(false);
  const lines = (content || '').split('\n').filter(Boolean);
  const handleMark = () => { if (!read) { setRead(true); onRead && onRead(); } };
  return (
    <div className="space-y-4">
      <div className="prose prose-invert max-w-none bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-6 space-y-3">
        {lines.map((line, i) => {
          if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="font-bold text-white text-sm">{line.slice(2, -2)}</p>;
          if (line.startsWith('**')) {
            const bold = line.match(/\*\*(.*?)\*\*/g);
            return <p key={i} className="text-sm text-[#CBD5E1]" dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />;
          }
          return <p key={i} className="text-sm text-[#CBD5E1]">{line}</p>;
        })}
      </div>
      {!read ? (
        <button onClick={handleMark} data-testid="mark-read-btn"
          className="w-full py-2.5 rounded-xl border border-indigo-500/40 text-indigo-400 text-sm font-medium hover:bg-indigo-500/10 transition">
          Marcar como leído
        </button>
      ) : (
        <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium justify-center">
          <CheckCircle size={15} /> Lectura completada
        </div>
      )}
    </div>
  );
}

// ─── Test Component ────────────────────────────────────────────────────────
function TestView({ lesson, onTestPassed, authFetch }) {
  const test = lesson?.test;
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const allAnswered = test?.questions && Object.keys(answers).length >= test.questions.length;

  const submitTest = async () => {
    if (!test) return;
    setLoading(true); setError('');
    try {
      const res = await authFetch(`${API}/academy/tests/${test.id}/attempt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error al enviar el test');
      setResults(data);
      if (data.passed) onTestPassed && onTestPassed();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  if (!test) return <div className="text-center py-10 text-[#94A3B8]">Test no disponible</div>;

  if (results) return (
    <div className="space-y-5" data-testid="test-results">
      <div className={`rounded-2xl p-6 text-center border ${results.passed ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
        <p className="text-5xl mb-2">{results.passed ? '🎉' : '💪'}</p>
        <p className="text-4xl font-black text-white">{results.score}%</p>
        <p className={`text-lg font-bold mt-1 ${results.passed ? 'text-emerald-400' : 'text-red-400'}`}>
          {results.passed ? '¡Aprobado!' : `No aprobado — mínimo 70%`}
        </p>
        <p className="text-xs text-[#94A3B8] mt-1">Intento {results.attempt_number} de {results.max_attempts}</p>
      </div>
      {results.results?.map((r, i) => (
        <div key={i} className={`rounded-xl p-4 border text-sm ${r.correct ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-red-500/30 bg-red-500/10'}`}>
          <div className="flex gap-2">
            <span className="flex-shrink-0">{r.correct ? '✅' : '❌'}</span>
            <p className="text-[#CBD5E1]">{r.explanation}</p>
          </div>
        </div>
      ))}
      {!results.passed && results.attempt_number < results.max_attempts && (
        <button onClick={() => { setResults(null); setAnswers({}); }} data-testid="retry-test-btn"
          className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold transition">
          Intentar de nuevo ({results.max_attempts - results.attempt_number} intentos restantes)
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-5" data-testid="test-view">
      <div className="flex items-center gap-3 bg-indigo-600/10 border border-indigo-500/30 rounded-xl p-4 text-sm text-indigo-300">
        <Star size={16} className="flex-shrink-0" />
        <span>Necesitas un 70% para aprobar · Tienes hasta 3 intentos</span>
      </div>
      {test.questions?.map((q, qi) => (
        <div key={q.id} className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-5 space-y-3" data-testid={`question-${qi}`}>
          <p className="text-white font-medium text-sm">{qi + 1}. {q.text}</p>
          {q.type === 'multiple' && (
            <div className="space-y-2">
              {q.options?.map((opt, oi) => (
                <button key={oi} onClick={() => setAnswers(p => ({ ...p, [q.id]: oi }))}
                  data-testid={`q${qi}-option-${oi}`}
                  className={`w-full text-left px-4 py-2.5 rounded-xl border transition text-sm ${answers[q.id] === oi ? 'border-indigo-500 bg-indigo-600/20 text-white' : 'border-[rgba(148,163,184,0.14)] text-[#94A3B8] hover:border-indigo-500/40'}`}>
                  <span className="font-mono text-xs mr-2">{String.fromCharCode(65 + oi)}.</span>{opt}
                </button>
              ))}
            </div>
          )}
          {q.type === 'true_false' && (
            <div className="flex gap-3">
              {[{ label: 'Verdadero', val: true }, { label: 'Falso', val: false }].map(({ label, val }) => (
                <button key={label} onClick={() => setAnswers(p => ({ ...p, [q.id]: val }))}
                  data-testid={`q${qi}-${label.toLowerCase()}`}
                  className={`flex-1 py-2.5 rounded-xl border transition text-sm font-medium ${answers[q.id] === val ? 'border-indigo-500 bg-indigo-600/20 text-white' : 'border-[rgba(148,163,184,0.14)] text-[#94A3B8] hover:border-indigo-500/40'}`}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
      {error && <p className="text-red-400 text-sm text-center">{error}</p>}
      <button onClick={submitTest} disabled={loading || !allAnswered} data-testid="submit-test-btn"
        className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold transition disabled:opacity-40">
        {loading ? 'Corrigiendo...' : `Enviar respuestas (${Object.keys(answers).length}/${test.questions?.length || 0})`}
      </button>
    </div>
  );
}

// ─── Consent Modal (Mediadores del Futuro) ────────────────────────────────
function ConsentModal({ courseCode, userId, courseName, onAccept }) {
  const [checked, setChecked] = useState(false);
  const [signing, setSigning] = useState(false);

  const handleSign = () => {
    setSigning(true);
    localStorage.setItem(`academy_consent_mdf_${userId}_${courseCode}`, '1');
    setTimeout(() => { setSigning(false); onAccept(); }, 700);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="max-w-lg w-full bg-[#080F20] border border-violet-500/30 rounded-3xl shadow-2xl shadow-violet-900/30 overflow-hidden">
        <div className="px-6 pt-7 pb-5 border-b border-violet-500/20 text-center bg-gradient-to-b from-violet-900/20 to-transparent">
          <div className="w-14 h-14 rounded-2xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center mx-auto mb-3">
            <Shield size={26} className="text-violet-400" />
          </div>
          <p className="text-[10px] uppercase tracking-widest text-violet-400 font-bold mb-1">Programa Mediadores del Futuro</p>
          <h2 className="text-xl font-black text-white">Consentimiento Digital</h2>
          <p className="text-[#94A3B8] text-xs mt-1">Antes de acceder a <strong className="text-white">{courseName}</strong></p>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-[rgba(148,163,184,0.04)] border border-[rgba(148,163,184,0.10)] rounded-2xl p-4 space-y-2 max-h-52 overflow-y-auto text-xs text-[#94A3B8] leading-relaxed custom-scroll">
            <p className="font-bold text-white mb-1">Términos del Programa</p>
            <p>• El Programa <strong className="text-violet-300">Mediadores del Futuro</strong> es un piloto social de Neuralia369 en alianza con WACA.</p>
            <p>• Este curso es para jóvenes de 12–17 años bajo supervisión de un mentor certificado.</p>
            <p>• Las entradas del <strong className="text-violet-300">Diario de Aprendizaje</strong> son privadas y solo accesibles al alumno y su mentor asignado.</p>
            <p>• No existe comunicación directa entre participantes dentro del programa.</p>
            <p>• El botón <strong className="text-violet-300">"Necesito Ayuda"</strong> notifica de forma confidencial al mentor sin guardar el motivo.</p>
            <p>• Los datos se tratan conforme a la Ley Peruana N.° 29733 de Protección de Datos Personales.</p>
            <p>• En caso de detectarse lenguaje de riesgo en el diario, el mentor recibirá una alerta automática para contactar al alumno.</p>
            <p>• El certificado se emite al completar todos los módulos con un mínimo del 70% en los tests evaluativos.</p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer group" onClick={() => setChecked(p => !p)}>
            <div className={`w-5 h-5 rounded-md border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-all ${checked ? 'bg-violet-600 border-violet-500' : 'border-[rgba(148,163,184,0.3)] group-hover:border-violet-500/70'}`}>
              {checked && <CheckCircle size={12} className="text-white" />}
            </div>
            <p className="text-xs text-[#94A3B8] leading-relaxed select-none">
              He leído y acepto las condiciones del programa. Autorizo el tratamiento de mis datos conforme a la política de privacidad de Neuralia369 Academy.
            </p>
          </label>

          <button onClick={handleSign} disabled={!checked || signing} data-testid="consent-sign-btn"
            className="w-full py-3.5 rounded-2xl bg-violet-600 hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold transition text-sm">
            {signing ? 'Firmando consentimiento...' : 'Firmar y acceder al curso'}
          </button>

          <p className="text-[10px] text-center text-[#94A3B8]/50">
            Alianza Neuralia369 · WACA · Firma digital registrada con fecha y hora
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Diario de Aprendizaje ─────────────────────────────────────────────────
function DiariodeAprendizaje({ lessonId, authFetch }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [loadedEntries, setLoadedEntries] = useState(false);

  useEffect(() => {
    if (!open || loadedEntries) return;
    authFetch(`${API}/academy/diary`).then(r => r.json())
      .then(d => { setEntries(d.entries || []); setLoadedEntries(true); })
      .catch(() => {});
  }, [open, loadedEntries, authFetch]);

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true); setSaved(null);
    try {
      const res = await authFetch(`${API}/academy/diary`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: text.trim(), lesson_id: lessonId })
      });
      const data = await res.json();
      if (data.success) {
        setSaved({ alert: data.alert });
        setEntries(p => [{ content: text.trim(), created_at: new Date().toISOString(), lesson_id: lessonId }, ...p]);
        setText('');
      }
    } catch (e) {}
    finally { setSaving(false); }
  };

  return (
    <div className="border border-violet-500/20 rounded-2xl overflow-hidden" data-testid="learning-diary">
      <button onClick={() => setOpen(p => !p)}
        className="w-full flex items-center justify-between px-5 py-4 bg-[rgba(109,40,217,0.06)] hover:bg-[rgba(109,40,217,0.10)] transition text-left group">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center">
            <BookText size={15} className="text-violet-400" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold">Mi Diario de Aprendizaje</p>
            <p className="text-[#94A3B8] text-xs">Privado · Solo visible para ti y tu mentor</p>
          </div>
        </div>
        <ChevronDown size={15} className={`text-[#94A3B8] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 pt-3 bg-[rgba(109,40,217,0.03)] space-y-4">
          <div className="space-y-2">
            <textarea value={text} onChange={e => setText(e.target.value)} rows={4}
              placeholder="¿Qué aprendiste hoy? ¿Cómo te sentiste? ¿Qué quieres recordar de esta lección?..."
              data-testid="diary-textarea"
              className="w-full bg-[rgba(8,15,32,0.8)] border border-[rgba(148,163,184,0.14)] focus:border-violet-500/50 rounded-xl px-4 py-3 text-white text-sm placeholder-[#94A3B8]/40 resize-none focus:outline-none transition" />
            {saved && (
              <div className={`text-xs px-3 py-2 rounded-xl ${saved.alert ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'}`}>
                {saved.alert ? '¡Tu entrada fue guardada. Tu mentor la revisará pronto.' : '¡Entrada guardada en tu diario!'}
              </div>
            )}
            <button onClick={save} disabled={!text.trim() || saving} data-testid="diary-save-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white text-xs font-bold transition">
              <Send size={12} /> {saving ? 'Guardando...' : 'Guardar entrada'}
            </button>
          </div>

          {entries.length > 0 && (
            <div className="space-y-2 border-t border-[rgba(148,163,184,0.08)] pt-4">
              <p className="text-[10px] text-violet-400 font-bold uppercase tracking-wider">Entradas anteriores ({entries.length})</p>
              {entries.slice(0, 5).map((e, i) => (
                <div key={i} className="bg-[rgba(8,15,32,0.6)] border border-violet-500/10 rounded-xl px-4 py-3">
                  <p className="text-white text-xs leading-relaxed">{e.content}</p>
                  <p className="text-[#94A3B8]/50 text-[10px] mt-1.5">{e.created_at?.slice(0,16).replace('T',' ')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Necesito Ayuda Button ─────────────────────────────────────────────────
function NecesitoAyudaButton({ authFetch, cohorteCode }) {
  const [open, setOpen] = useState(false);
  const [helpLines, setHelpLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notified, setNotified] = useState(false);

  const handlePress = async () => {
    setOpen(true);
    if (notified) return;
    setLoading(true);
    try {
      const res = await authFetch(`${API}/academy/help/request`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cohorte_code: cohorteCode || '' })
      });
      const data = await res.json();
      setHelpLines(data.help_lines || []);
      setNotified(true);
    } catch (e) {
      setHelpLines([
        { country: 'Perú', name: 'Línea 100 — MIMP', number: '100' },
        { country: 'Global', name: 'Crisis Text Line', number: 'Text HOME to 741741' },
      ]);
    }
    setLoading(false);
  };

  return (
    <>
      <button onClick={handlePress} data-testid="necesito-ayuda-btn"
        className="fixed bottom-6 left-6 z-40 flex items-center gap-2 px-4 py-3 rounded-2xl bg-red-600 hover:bg-red-500 text-white text-sm font-bold shadow-xl shadow-red-900/40 transition hover:scale-105 active:scale-95 border border-red-500/50">
        <HelpCircle size={16} />
        Necesito Ayuda
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="help-modal">
          <div className="w-full max-w-md bg-[#080F20] border border-red-500/30 rounded-3xl shadow-2xl p-6 space-y-5">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-red-600/20 flex items-center justify-center flex-shrink-0">
                  <HelpCircle size={22} className="text-red-400" />
                </div>
                <div>
                  <p className="text-white font-bold">¿Necesitas Ayuda?</p>
                  {loading && <p className="text-[#94A3B8] text-xs">Notificando a tu mentor...</p>}
                  {notified && <p className="text-emerald-400 text-xs font-medium">Tu mentor ha sido notificado</p>}
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-white p-1 transition"><X size={18} /></button>
            </div>

            <p className="text-[#94A3B8] text-xs bg-[rgba(148,163,184,0.05)] rounded-xl p-3 border border-[rgba(148,163,184,0.1)]">
              Recuerda: <strong className="text-white">no estás solo/a</strong>. Aquí tienes líneas de apoyo disponibles ahora mismo:
            </p>

            <div className="space-y-2">
              {(helpLines.length > 0 ? helpLines : [
                { country: 'Perú', name: 'Línea 100 — MIMP', number: '100' },
                { country: 'México', name: 'SAPTEL', number: '55 5259-8121' },
                { country: 'Chile', name: 'Fono Orientación', number: '800 200 818' },
                { country: 'Global', name: 'Crisis Text Line', number: 'Text HOME to 741741' },
              ]).map((hl, i) => (
                <div key={i} className="flex items-center justify-between bg-white/5 border border-[rgba(148,163,184,0.10)] rounded-xl px-4 py-3 hover:bg-white/8 transition">
                  <div>
                    <p className="text-white text-sm font-semibold">{hl.name}</p>
                    <p className="text-[#94A3B8] text-xs">{hl.country}</p>
                  </div>
                  <a href={`tel:${hl.number}`} className="text-emerald-400 font-bold text-sm hover:text-emerald-300 transition">
                    {hl.number}
                  </a>
                </div>
              ))}
            </div>

            <p className="text-[10px] text-center text-[#94A3B8]/50">
              Programa Mediadores del Futuro · Neuralia369 Academy × WACA
            </p>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Aynara PNL/VAK Classroom Chat ────────────────────────────────────────
const VAK_LABELS = {
  video: { label: 'Visual', color: 'bg-blue-500/20 text-blue-400', icon: '🎬' },
  lectura: { label: 'Auditivo', color: 'bg-emerald-500/20 text-emerald-400', icon: '🎧' },
  test: { label: 'Kinestésico', color: 'bg-amber-500/20 text-amber-400', icon: '✋' },
  proyecto: { label: 'Kinestésico', color: 'bg-orange-500/20 text-orange-400', icon: '✋' },
  actividad: { label: 'Kinestésico', color: 'bg-orange-500/20 text-orange-400', icon: '✋' },
};

function AynaraClassroomChat({ authFetch, courseName, moduleName, lessonName, lessonType }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [vakStyle, setVakStyle] = useState('');
  const vakInfo = VAK_LABELS[lessonType] || null;

  // Greeting on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        text: `¡Hola! Soy Aynara, tu tutora de IA 🌟\n\nEstás en **${moduleName}** — "${lessonName}". ¿Tienes alguna pregunta sobre este tema o quieres que te explique algo usando PNL y VAK?`
      }]);
    }
  }, [open, messages.length, moduleName, lessonName]);

  const ask = async () => {
    if (!input.trim() || loading) return;
    const q = input.trim();
    setMessages(p => [...p, { role: 'user', text: q }]);
    setInput(''); setLoading(true);
    try {
      const res = await authFetch(`${API}/academy/aynara/ask`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: q, course_name: courseName,
          module_name: moduleName, lesson_name: lessonName,
          vak_style: vakStyle
        })
      });
      const data = await res.json();
      setMessages(p => [...p, { role: 'assistant', text: data.response || '(Sin respuesta)' }]);
    } catch (e) {
      setMessages(p => [...p, { role: 'assistant', text: 'En este momento no puedo responder. Inténtalo de nuevo.' }]);
    }
    setLoading(false);
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="aynara-chat-btn"
      className="fixed bottom-6 right-6 z-40 flex items-center gap-2 px-4 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold shadow-xl shadow-indigo-900/40 transition hover:scale-105 active:scale-95 border border-indigo-400/30">
      <Sparkles size={16} />
      Pregúntale a Aynara
      {vakInfo && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${vakInfo.color}`}>{vakInfo.icon} {vakInfo.label}</span>}
    </button>
  );

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col w-[360px] max-w-[calc(100vw-2rem)] h-[480px] bg-[#080F20] border border-indigo-500/30 rounded-3xl shadow-2xl shadow-indigo-900/30 overflow-hidden" data-testid="aynara-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-indigo-500/20 bg-gradient-to-r from-indigo-900/40 to-violet-900/40">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-indigo-600/30 flex items-center justify-center">
            <Sparkles size={14} className="text-indigo-400" />
          </div>
          <div>
            <p className="text-white text-sm font-bold">Aynara</p>
            <p className="text-indigo-300 text-[10px]">Tutora PNL/VAK · Neuralia369</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {vakInfo && (
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${vakInfo.color}`}>{vakInfo.icon} {vakInfo.label}</span>
          )}
          <button onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-white p-1 transition"><Minimize2 size={14} /></button>
        </div>
      </div>

      {/* VAK selector */}
      <div className="flex gap-1 px-3 py-2 border-b border-[rgba(148,163,184,0.08)]">
        <p className="text-[10px] text-[#94A3B8] mr-1 flex items-center">Mi estilo:</p>
        {[['visual','🎬','Visual'],['auditivo','🎧','Auditivo'],['kinestesico','✋','Kines.']].map(([v,e,l]) => (
          <button key={v} onClick={() => setVakStyle(vk => vk === v ? '' : v)}
            className={`text-[10px] px-2 py-1 rounded-lg border transition ${vakStyle === v ? 'bg-indigo-600 border-indigo-500 text-white' : 'border-[rgba(148,163,184,0.14)] text-[#94A3B8] hover:border-indigo-500/40'}`}>
            {e} {l}
          </button>
        ))}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 text-sm">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-xs leading-relaxed whitespace-pre-wrap ${
              m.role === 'user'
                ? 'bg-indigo-600 text-white rounded-br-sm'
                : 'bg-[rgba(148,163,184,0.08)] border border-[rgba(148,163,184,0.10)] text-[#E2E8F0] rounded-bl-sm'
            }`}>
              {m.text}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-[rgba(148,163,184,0.08)] border border-[rgba(148,163,184,0.10)] rounded-2xl rounded-bl-sm px-4 py-2.5">
              <div className="flex gap-1">
                {[0,1,2].map(i => <div key={i} className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{animationDelay:`${i*0.15}s`}} />)}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex gap-2 p-3 border-t border-[rgba(148,163,184,0.08)]">
        <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && ask()}
          placeholder="Pregunta algo sobre esta lección..."
          data-testid="aynara-input"
          className="flex-1 bg-[rgba(8,15,32,0.8)] border border-[rgba(148,163,184,0.14)] focus:border-indigo-500/50 rounded-xl px-3 py-2 text-white text-xs placeholder-[#94A3B8]/40 focus:outline-none transition" />
        <button onClick={ask} disabled={!input.trim() || loading} data-testid="aynara-send-btn"
          className="w-8 h-8 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white flex items-center justify-center flex-shrink-0 transition">
          <Send size={13} />
        </button>
      </div>
    </div>
  );
}

// ─── K0 Achievements ────────────────────────────────────────────────────────
const K0_ACHIEVEMENTS = [
  { emoji: '🤖', title: 'Las máquinas que ayudan' },
  { emoji: '🧑‍🤝‍🧑', title: 'Personas y robots' },
  { emoji: '👁️', title: 'Ver y reconocer' },
  { emoji: '🗣️', title: 'Escuchar y hablar' },
  { emoji: '📚', title: 'Aprender con ejemplos' },
  { emoji: '❌', title: 'Los robots se equivocan' },
  { emoji: '🛡️', title: 'Cuidarnos' },
  { emoji: '✏️', title: 'Mi robot ayudante' },
];

function K0AchievementsView({ completedCount, onComplete }) {
  const [justEarned, setJustEarned] = useState(false);
  const handleComplete = () => { setJustEarned(true); onComplete && onComplete(); };
  return (
    <div className="space-y-6" data-testid="k0-achievements">
      <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-2xl p-6 text-center">
        <p className="text-4xl mb-2">🏆</p>
        <h3 className="text-xl font-black text-white">Mis Logros de Nuri</h3>
        <p className="text-[#94A3B8] text-sm mt-1">{completedCount} de 8 pegatinas conseguidas</p>
        <div className="mt-3 h-3 bg-white/10 rounded-full overflow-hidden">
          <div className="h-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-700"
            style={{ width: `${(completedCount / 8) * 100}%` }} />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {K0_ACHIEVEMENTS.map((a, i) => {
          const earned = i < completedCount;
          return (
            <div key={i} className={`aspect-square rounded-2xl flex flex-col items-center justify-center p-2 text-center border-2 transition ${earned ? 'border-amber-400 bg-amber-400/10' : 'border-[rgba(148,163,184,0.14)] bg-[rgba(8,15,32,0.4)] opacity-50'}`}
              data-testid={`achievement-${i}-${earned ? 'earned' : 'locked'}`}>
              <span className="text-3xl">{earned ? a.emoji : '🔒'}</span>
              <p className="text-[9px] text-[#94A3B8] mt-1 leading-tight">{a.title}</p>
            </div>
          );
        })}
      </div>

      {justEarned ? (
        <div className="bg-amber-400/20 border border-amber-400/40 rounded-2xl p-4 text-center">
          <p className="text-2xl mb-1">🎉</p>
          <p className="text-amber-300 font-bold">¡Pegatina conseguida!</p>
        </div>
      ) : (
        <button onClick={handleComplete} data-testid="k0-complete-activity"
          className="w-full py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold text-base hover:opacity-90 transition">
          ¡Completar actividad y ganar pegatina!
        </button>
      )}
    </div>
  );
}

// ─── Main Classroom Page ───────────────────────────────────────────────────
export default function AcademyClassroomPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, authFetch, loading: authLoading } = useAuth();
  const [course, setCourse] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [lessonData, setLessonData] = useState(null);
  const [completedLessons, setCompletedLessons] = useState([]);
  const [progressPct, setProgressPct] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingLesson, setLoadingLesson] = useState(false);
  const [showConsent, setShowConsent] = useState(false);

  const isKids = course ? ['kids'].includes(course.line) : false;
  const isK0 = course?.code === 'K0';
  const isSocial = course?.line === 'social' || course?.code?.startsWith('N');
  const canUseAynara = user && !user.is_minor && (user.age_range ? parseInt(user.age_range.split('-')[0]) >= 13 : true) && !isKids;

  // Flat list of all lessons in order
  const allLessons = course?.modules?.flatMap(m => m.lessons || []) || [];

  useEffect(() => {
    if (authLoading) return; // Wait for auth to load before checking
    if (!user) { navigate('/acceso-academy'); return; }
    const load = async () => {
      try {
        const [cRes, eRes] = await Promise.all([
          fetch(`${API}/academy/courses/${courseId}`),
          authFetch(`${API}/academy/my-courses`)
        ]);
        const cData = await cRes.json();
        const eData = await eRes.json();
        if (!cRes.ok) { navigate(`/academy/${courseId}`); return; }
        const enroll = eData.enrollments?.find(e => e.course_id === courseId?.toUpperCase());
        if (!enroll) { navigate(`/academy/${courseId}`); return; }
        setCourse(cData);
        setEnrollment(enroll);
        setCompletedLessons(enroll.completed_lessons || []);
        setProgressPct(enroll.progress_pct || 0);
        // Check consent for social/mediadores courses
        const isSocialCourse = cData.line === 'social' || cData.code?.startsWith('N');
        if (isSocialCourse && user) {
          const consentKey = `academy_consent_mdf_${user.id || user.email}_${cData.code}`;
          if (!localStorage.getItem(consentKey)) setShowConsent(true);
        }
        // Start at first incomplete or first lesson
        const allL = cData.modules?.flatMap(m => m.lessons || []) || [];
        const firstIncomplete = allL.find(l => !(enroll.completed_lessons || []).includes(l.id));
        const startLesson = firstIncomplete || allL[0];
        if (startLesson) setCurrentLesson(startLesson);
      } catch (e) {
        navigate(`/academy/${courseId}`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user, authLoading, courseId, navigate, authFetch]);

  useEffect(() => {
    if (!currentLesson || !user) return;
    const fetchLesson = async () => {
      setLoadingLesson(true);
      try {
        const res = await authFetch(`${API}/academy/courses/${courseId}/lessons/${currentLesson.id}`);
        const data = await res.json();
        setLessonData(data);
      } catch (e) { setLessonData(currentLesson); }
      finally { setLoadingLesson(false); }
    };
    fetchLesson();
  }, [currentLesson, courseId, user, authFetch]);

  const markComplete = useCallback(async () => {
    if (!currentLesson || completedLessons.includes(currentLesson.id)) return;
    try {
      const res = await authFetch(`${API}/academy/progress`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_id: courseId, lesson_id: currentLesson.id })
      });
      const data = await res.json();
      if (data.success) {
        setCompletedLessons(p => [...p, currentLesson.id]);
        setProgressPct(data.progress_pct);
      }
    } catch (e) {}
  }, [currentLesson, completedLessons, courseId, authFetch]);

  const goToLesson = (lesson) => setCurrentLesson(lesson);
  const currentIndex = allLessons.findIndex(l => l.id === currentLesson?.id);
  const prevLesson = currentIndex > 0 ? allLessons[currentIndex - 1] : null;
  const nextLesson = currentIndex < allLessons.length - 1 ? allLessons[currentIndex + 1] : null;

  if (loading) return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center">
      <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const LessonIcon = LESSON_TYPE_ICON[currentLesson?.type] || FileText;

  return (
    <div className="min-h-screen bg-[#030712] flex flex-col" data-testid="academy-classroom">
      {/* Consent modal for social/mediadores courses */}
      {showConsent && course && (
        <ConsentModal
          courseCode={course.code}
          userId={user?.id || user?.email || ''}
          courseName={course.name}
          onAccept={() => setShowConsent(false)}
        />
      )}
      {/* Top bar */}
      <div className="h-14 bg-[rgba(8,15,32,0.95)] border-b border-[rgba(148,163,184,0.14)] flex items-center justify-between px-4 sticky top-0 z-30">
        <div className="flex items-center gap-3">
          <button onClick={() => setSidebarOpen(o => !o)} className="text-[#94A3B8] hover:text-white transition lg:hidden">
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <Link to={`/academy/${courseId}`} className="text-[#94A3B8] hover:text-white transition">
            <ChevronLeft size={18} />
          </Link>
          <div>
            <p className="text-xs text-[#94A3B8] hidden sm:block">{course?.name}</p>
            <p className="text-sm font-semibold text-white truncate max-w-[200px] sm:max-w-none">{currentLesson?.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex items-center gap-2">
            <div className="w-32 h-1.5 bg-white/10 rounded-full">
              <div className="h-1.5 bg-indigo-500 rounded-full transition-all" style={{ width: `${progressPct}%` }} />
            </div>
            <span className="text-xs text-[#94A3B8]">{progressPct}%</span>
          </div>
          <Link to="/academy/mi-perfil" className="text-xs text-[#94A3B8] hover:text-white transition hidden sm:block">Mi perfil</Link>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className={`${sidebarOpen ? 'w-72 lg:w-64' : 'w-0'} flex-shrink-0 overflow-hidden transition-all duration-300 bg-[rgba(8,15,32,0.95)] border-r border-[rgba(148,163,184,0.14)] flex flex-col`}
          data-testid="classroom-sidebar">
          <div className="overflow-y-auto flex-1 py-4 px-2 space-y-1">
            {course?.modules?.map((mod, mi) => (
              <div key={mod.id}>
                <div className="px-3 py-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-wider">
                  M{mi + 1}: {mod.title}
                </div>
                {mod.lessons?.map(l => {
                  const LIcon = LESSON_TYPE_ICON[l.type] || FileText;
                  const isComplete = completedLessons.includes(l.id);
                  const isCurrent = l.id === currentLesson?.id;
                  return (
                    <button key={l.id} onClick={() => goToLesson(l)} data-testid={`sidebar-lesson-${l.id}`}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-left transition text-xs ${isCurrent ? 'bg-indigo-600/30 text-white' : 'text-[#94A3B8] hover:bg-white/5 hover:text-white'}`}>
                      <div className={`w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0 ${isComplete ? 'bg-emerald-500/20' : 'bg-white/5'}`}>
                        {isComplete ? <CheckCircle size={11} className="text-emerald-400" /> : <LIcon size={11} />}
                      </div>
                      <span className="truncate">{l.title}</span>
                      {l.duration_min && <span className="ml-auto text-[10px] text-[#94A3B8]/60 flex-shrink-0">{l.duration_min}m</span>}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto px-4 py-6 max-w-3xl mx-auto w-full space-y-6">          {loadingLesson ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Lesson header */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs text-[#94A3B8]">
                  <LessonIcon size={13} />
                  <span>{LESSON_TYPE_LABEL[currentLesson?.type] || 'Lección'}</span>
                  {currentLesson?.duration_min && <span>· {currentLesson.duration_min} min</span>}
                </div>
                <h2 className="text-2xl font-black text-white">{currentLesson?.title}</h2>
                {currentLesson?.description && <p className="text-[#94A3B8] text-sm">{currentLesson.description}</p>}
              </div>

              {/* Content by type */}
              {isK0 ? (
                <K0AchievementsView
                  completedCount={completedLessons.length}
                  onComplete={markComplete}
                />
              ) : currentLesson?.type === 'video' ? (
                <VideoPlayer url={lessonData?.video_url} onWatched={markComplete} />
              ) : currentLesson?.type === 'lectura' ? (
                <ReadingContent content={lessonData?.content} onRead={markComplete} />
              ) : currentLesson?.type === 'test' ? (
                <TestView lesson={lessonData} onTestPassed={markComplete} authFetch={authFetch} />
              ) : currentLesson?.type === 'proyecto' ? (
                <div className="bg-[rgba(8,15,32,0.6)] border border-[rgba(148,163,184,0.14)] rounded-2xl p-6 space-y-4">
                  <h3 className="text-lg font-bold text-white">Proyecto Final</h3>
                  <p className="text-[#94A3B8] text-sm">{lessonData?.description}</p>
                  {lessonData?.rubric && (
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-white">Rúbrica de evaluación</p>
                      {lessonData.rubric.map((r, i) => (
                        <div key={i} className="flex justify-between items-center py-2 border-b border-[rgba(148,163,184,0.08)] text-sm">
                          <span className="text-[#94A3B8]">{r.criterion}</span>
                          <span className="text-white font-bold">{r.max_score} pts</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={markComplete} data-testid="mark-project-done"
                    className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold transition">
                    Marcar proyecto como completado
                  </button>
                </div>
              ) : null}

              {/* Aynara CTA (only for adults 13+) */}
              {canUseAynara && (
                <div className="flex items-center gap-3 bg-indigo-600/10 border border-indigo-500/20 rounded-2xl p-4" data-testid="aynara-cta">
                  <Sparkles size={18} className="text-indigo-400 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">¿Tienes dudas?</p>
                    <p className="text-xs text-[#94A3B8]">Pregúntale a Aynara sobre esta lección</p>
                  </div>
                  <button className="text-xs bg-indigo-600 hover:bg-indigo-500 text-white px-3 py-1.5 rounded-lg transition font-medium" data-testid="ask-aynara-btn">
                    Preguntar
                  </button>
                </div>
              )}

              {/* Certificate CTA */}
              {progressPct === 100 && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5 text-center space-y-3" data-testid="certificate-cta">
                  <p className="text-3xl">🎓</p>
                  <p className="text-white font-bold text-lg">¡Curso completado!</p>
                  <p className="text-[#94A3B8] text-sm">Ya puedes descargar tu certificado</p>
                  <Link to="/academy/mi-perfil" className="inline-block px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-bold text-sm transition" data-testid="go-to-cert-btn">
                    Ver mi certificado
                  </Link>
                </div>
              )}

              {/* Diario de Aprendizaje — solo cursos sociales/Mediadores */}
              {isSocial && !isKids && currentLesson && (
                <DiariodeAprendizaje lessonId={currentLesson.id} authFetch={authFetch} />
              )}

              {/* Prev / Next navigation */}
              <div className="flex gap-3 pt-2">
                {prevLesson ? (
                  <button onClick={() => goToLesson(prevLesson)} data-testid="prev-lesson-btn"
                    className="flex-1 flex items-center gap-2 px-4 py-3 rounded-xl border border-[rgba(148,163,184,0.14)] text-[#94A3B8] hover:text-white hover:border-white/20 transition text-sm">
                    <ChevronLeft size={15} /> Anterior
                  </button>
                ) : <div className="flex-1" />}
                {nextLesson && (
                  <button onClick={() => goToLesson(nextLesson)} data-testid="next-lesson-btn"
                    className="flex-1 flex items-center justify-end gap-2 px-4 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white transition text-sm font-medium">
                    Siguiente <ChevronRight size={15} />
                  </button>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      {/* Floating "Necesito Ayuda" button — solo cursos sociales/Mediadores */}
      {isSocial && (
        <NecesitoAyudaButton authFetch={authFetch} cohorteCode={enrollment?.cohorte_code || ''} />
      )}

      {/* Aynara PNL/VAK chat — solo usuarios adultos (no menores) */}
      {!user?.is_minor && !isKids && canUseAynara && currentLesson && (
        <AynaraClassroomChat
          authFetch={authFetch}
          courseName={course?.name || ''}
          moduleName={currentLesson?.module_id?.replace('-', ' ') || ''}
          lessonName={currentLesson?.title || ''}
          lessonType={currentLesson?.type || ''}
        />
      )}
    </div>
  );
}
